import {
	useContext,
	ReactNode,
	useLayoutEffect,
	createContext,
	useState,
} from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { AppUser, Client, Merchant, UserType } from '@/types/user';
import {
	LoginValues,
	SubmitStatus,
} from '@/components/views/unauthenticated/login/Login';
import { useQueryClient } from '@tanstack/react-query';
import { isAppError } from '@/utils/utilities';
import { AxiosError } from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { ErrorCode, UUID } from '@/types/types';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import httpClient from '@/api/httpClient';
import StorageKey from '@/configs/storageKey';
import Cookies from 'js-cookie';
import UserApi from '@/api/UserApi';

type LoginResponse =
	| { status: SubmitStatus.SUCCESS }
	| { status: SubmitStatus.ERROR; stack: (ErrorCode | string)[] };

interface TokenPayload extends JwtPayload {
	id: UUID;
	sessionId: UUID;
	userType: UserType;
}

interface Context {
	remember: boolean;
	setRemember: (value: boolean) => void;
	user: AppUser | null;
	changeUser: (newUser?: AppUser | null) => void;
	upateUser: (data: Partial<AppUser>) => void;
	login: (values: LoginValues) => Promise<LoginResponse>;
	logout: () => void;
	isClient: (user: AppUser) => user is Client;
	isMerchant: (user: AppUser) => user is Merchant;
}

const AuthContext = createContext<Context>(null!);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = useQueryClient();

	const [remember, setRemember] = useLocalStorage({
		key: StorageKey.REMEMBER,
		defaultValue: true,
		getInitialValueInEffect: false,
	});

	const [user, setUser] = useLocalStorage<AppUser | null>({
		key: StorageKey.USER,
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	const [refreshTimeout, setRefreshTimeout] = useState<number>();

	useLayoutEffect(() => {
		loginWithResfreshToken();
	}, []);

	const loginWithResfreshToken = async () => {
		const refresh = Cookies.get(StorageKey.REFRESH_TOKEN);
		if (!refresh) return;

		try {
			const response = await UnauthenticatedApi.refreshToken(refresh);

			const { token, refreshToken } = response.data;

			const decoded = jwtDecode<TokenPayload>(token);

			await setAuth(decoded, token, refreshToken);
		} catch (error: any) {
			console.error(error);
			if (error.code !== AxiosError.ECONNABORTED) logout();
		}
	};

	const refreshTokenWhenExpire = (token: TokenPayload) => {
		const tokenLifeSpan = token.iat && token.exp ? token.exp - token.iat : 0;
		// Refresh 5 sec before expire
		const refreshTimeout = (tokenLifeSpan - 5) * 1000;

		const id = setTimeout(() => {
			loginWithResfreshToken();
		}, refreshTimeout);

		setRefreshTimeout(id);
	};

	const login: Context['login'] = async ({ email, password }) => {
		try {
			const response = await UnauthenticatedApi.login({ email, password });

			const { token, refreshToken } = response.data;

			const decoded = jwtDecode<TokenPayload>(token);

			if (decoded.userType === UserType.ADMIN)
				throw new Error('ADMIN_NOT_ALLOWED');

			await setAuth(decoded, token, refreshToken);

			return { status: SubmitStatus.SUCCESS };
		} catch (error) {
			const stack = isAppError(error) ? error.stack : [ErrorCode.GENERIC];
			return { stack, status: SubmitStatus.ERROR };
		}
	};

	const logout: Context['logout'] = () => {
		UnauthenticatedApi.logout();
		queryClient.cancelQueries();
		Cookies.remove(StorageKey.REFRESH_TOKEN);
		httpClient.defaults.headers.common['Authorization'] = '';
		setUser(null);
		queryClient.clear();
		clearTimeout(refreshTimeout);
	};

	const setAuth = async (
		decoded: TokenPayload,
		token: string,
		refreshToken: string
	) => {
		setAuthHeader(token);

		if (decoded.userType === UserType.ADMIN)
			throw new Error('ADMIN_NOT_ALLOWED');

		const appUser = await UserApi.getCurrentUser();

		Cookies.set(StorageKey.REFRESH_TOKEN, refreshToken, {
			secure: true,
			expires: remember ? 360 : undefined,
		});

		refreshTokenWhenExpire(decoded);

		setUser(appUser);
	};

	const changeUser: Context['changeUser'] = (newUser = null) =>
		setUser(newUser);

	const upateUser: Context['upateUser'] = (data) =>
		setUser((prev) => (prev ? ({ ...prev, ...data } as AppUser) : null));

	const isClient: Context['isClient'] = (user) =>
		user.userType === UserType.CLIENT;

	const isMerchant: Context['isMerchant'] = (user) =>
		user.userType === UserType.MERCHANT;

	return (
		<AuthContext.Provider
			value={{
				remember,
				setRemember,
				user,
				changeUser,
				upateUser,
				login,
				logout,
				isClient,
				isMerchant,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

function setAuthHeader(token: string) {
	httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
